import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { BookOpen, Check, CloudOff, LogOut, Repeat, Settings as SettingsIcon } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { mediaUrl } from "../lib/api";
import { initialsFrom } from "../lib/utils";
import { signOutAndGoHome, SIGN_OUT_FAILED_MESSAGE } from "../lib/signOut";
import { homePathForRole } from "../lib/homePath";
import { markNav, track } from "../lib/usageTracker";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

// The label a role reads as in the switch. Local to this control on purpose
// — Layout.jsx has its own `roleLabel` for the demo banner, and the two are
// allowed to phrase things differently without sharing a module.
function roleLabel(role) {
  if (role === "coach") return "Coach";
  if (role === "admin") return "Admin";
  return "Player";
}

/**
 * The topbar account menu (issue #318) — Settings · Sign out, behind the
 * username/avatar, for EVERY role.
 *
 * What it replaces: an inert `<div data-testid="topbar-user">` carrying a
 * name and an avatar and nothing else. Clicking your own name is the one
 * affordance every user of every web app tries first, and in this app it
 * did nothing — so the account page was reachable only from a Dashboard
 * button that disappears once used, and Sign out existed only inside the
 * `hidden md:flex` sidebar, which is why a phone session could not be
 * ended at all (#262).
 *
 * #338 REMOVED THE "PROFILE" ITEM, and that is the point rather than a
 * loss. Martin's decision 4 on #287 is "one page": `/profile` now redirects
 * to `/settings`, so a Profile item would be a second name for the item
 * directly below it — two rows in a three-row menu leading to the same
 * screen, which is a worse version of the "two things called some version
 * of your account" confusion #318 existed to remove. The profile did not
 * lose its door; it is the second section of the page Settings opens, and
 * the affordance that means "go and fill this in" — the Dashboard's
 * Complete profile button — now links straight to it
 * (`/settings#player-profile`).
 *
 * ROLE-BLIND, WITH ONE NAMED EXCEPTION (#962, D177; narrowed by #1119,
 * D216). Every role still gets Settings and Sign out — what differs by role
 * is what Settings RENDERS (a player's cricket profile, a coach's coaching
 * profile, neither for an admin), not whether the item is there: a menu that
 * varied THAT per role would be back to the inert-avatar problem for
 * whichever role it thinned out. The Training item below is the deliberate
 * exception, not a crack in the rule: at 390 the coach's ONLY nav surface
 * is `AccountMenu` while the drawn coach bar is up (D173.C1 — Layout.jsx's
 * mobile strip is hidden exactly there) until the coach's own ribbon lands
 * (#1121). A player no longer needs it: Training & guides is the fourth
 * item of the player's ribbon (#1119, D214.1, D216), so #1039's player copy
 * is gone. The admin menu is UNCHANGED (D183, if it ever
 * extends this naming to admins, is #950's scope, not this one's). The one
 * OTHER role check in this topbar remains NotificationsBell's (#189), which
 * is about a feed that is empty by construction, not about identity.
 *
 * Radix DropdownMenu (`ui/dropdown-menu.jsx`), not the Popover the
 * notification panel (#189) and AccessibilityMenu use. Same Radix
 * primitive family, same portal/collision/Escape/click-outside/focus-return
 * behaviour — but a menu of ACTIONS also needs roving focus and type-ahead
 * over `role="menuitem"` children, which `DropdownMenu` gives for free and
 * `Popover` (a generic container, `role="dialog"`) does not. Using Popover
 * here would mean re-implementing arrow-key navigation by hand, which is
 * precisely what #189's "reuse the primitive" instruction exists to avoid.
 *
 * `empty` (#780, D140) — the drawn account circle on every round-5 screen
 * (`EmptyRecord.dc.html`/`OneInnings.dc.html`, sha256 7b8c77f7…/4d81a43d…,
 * the same shape `CoachPortalEmpty.dc.html`/the log-flow artboards draw):
 * 26px, `background: #E8F1FB`, `border: 1px solid #CBD5E1`, no initials, no
 * photo. That is a DIFFERENT trigger from the one every screen renders
 * today (`h-9 w-9`, a 2px ring, initials or a photo) — #780's own audit
 * found the initials avatar on every "built" screen against the drawn
 * empty circle on every one of them, this control included. Gated behind a
 * prop, default `false` (today's avatar, byte-identical), rather than
 * flipped unconditionally: this same component is `Layout.jsx`'s REAL
 * topbar avatar (see `OnboardingNew.jsx`'s own docstring on why the kit's
 * decorative `TopBar` placeholder is never used instead) for EVERY route,
 * flag on or off, so an unconditional change here would repaint a screen
 * this issue was never asked to touch. Only the trigger's own visual
 * changes — the menu it opens (Settings · Sign out · role switch) is
 * exactly the same menu either way, per the issue's own "still the account
 * menu trigger, with the same menu, just empty".
 *
 * The trigger `<button>` grows to a 44px tap target when `empty` (the
 * drawn 26px circle centred inside it) — same reasoning as
 * `AccessibilityMenu.jsx`'s own `hideTextSize`-gated resize: the SHIPPED
 * `h-9 w-9` avatar (36px) already sat close to a 44px hit area with its own
 * padding, but the smaller drawn circle needs the padding stated
 * explicitly rather than inherited from a bigger avatar that is no longer
 * there.
 *
 * PR #887 review, B3 (Andreas's ruling, D160) — the negative-margin
 * technique review round 2 (B2) used here is gone: `width: var(--nw-size-tap)` (32 before #1118), no
 * explicit height. `EndControlsRow` (kit/TopBar.jsx) gives this button's
 * own parent the bar's full height, and flexbox's default
 * `align-items: stretch` fills this child to match it for free, so the
 * tap area is exactly 44px wide × the full bar height, tiled edge to edge
 * with its neighbours — no overlap. The negative-margin 44px box DID
 * overlap its neighbours (found live: a click just right of the contrast
 * glyph opened this menu instead of the accessibility one).
 */
/**
 * Issue #892 (D171 X2) — see `NotificationsBell.jsx`'s own
 * `compactFocusRingFor` for the full reasoning (a `ring` clips against this
 * control's own D160 44×barHeight tap area (32 before #1118); a `-2px` `outline` sits inside
 * it instead). Duplicated rather than imported: the two files share no
 * module today, and a three-line focus helper is not worth a new one.
 */
const COMPACT_FOCUS_RING =
  // #931 — `nw-topbar-focus` is a marker class only (no rules of its own
  // here): `index.css`'s `html.hc-theme .nw-topbar-focus:focus-visible`
  // targets exactly these four controls so the hc-theme global rule's
  // outset ring can be pulled inside without touching every other
  // focusable element on the page.
  "focus-visible:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] nw-topbar-focus";
// #931, D171 X2 ruling (already-decided: "white on navy and navy on
// white" applies under hc-theme too, per-surface exactly like the non-hc
// outline colour two lines below) — `nw-topbar-focus-hc-navy` only on the
// white-surface branch swaps the hc-theme global rule's gold for
// `var(--nw-navy)` (>=3:1 against the white bar; gold measured ~2.38:1
// and was ruled out). The navy-surface branch keeps the global rule's
// gold untouched (already ~7.3:1 against the navy bar).
const compactFocusRingFor = (surface) =>
  surface === "white" ? `${COMPACT_FOCUS_RING} focus-visible:outline-[#0B2E59] nw-topbar-focus-hc-navy` : `${COMPACT_FOCUS_RING} focus-visible:outline-white`;

/** `TopBarPanels.dc.html`'s `account-menu-signout-error`: plain 12px ink
 * text under the item with a cloud-off glyph in the alert colour; no
 * background, no left rule. A polite live region rather than role="alert",
 * which is not valid inside role="menu". */
function AccountMenuSignOutError({ children }) {
  return (
    <div
      data-testid="account-menu-signout-error"
      style={{ display: "flex", gap: 8, padding: "10px 14px 14px", fontSize: 12, lineHeight: 1.5, color: "#0F172A" }}
    >
      <CloudOff size={16} color="#B42318" aria-hidden="true" style={{ flexShrink: 0, marginTop: 1 }} />
      <span aria-live="polite">{children}</span>
    </div>
  );
}

// `hasRibbon` — set by every screen that draws the player ribbon (`kit/
// Ribbon.jsx`, #1119): there Training & guides is the ribbon's fourth item,
// so the menu leaves it out. Everywhere else (the old look, D217, and any
// player screen without a ribbon) a player keeps the menu item as before.
// The coach never has the item (#1121): the coach ribbon carries it.
export function AccountMenu({ empty = false, surface = "navy", hasRibbon = false }) {
  const { user, logout, switchActiveRole } = useAuth();
  const navigate = useNavigate();
  const [signingOut, setSigningOut] = useState(false);
  // D57 / TopBarPanels.dc.html `sign-out-failed`: a refused sign-out is
  // said under the item, inside the open menu, in lib/signOut.js's own
  // sentence. Never a toast (`silent: true` below).
  const [signOutError, setSignOutError] = useState("");
  const [switchingTo, setSwitchingTo] = useState(null);

  // Issue #507: the hat switch, shown ONLY for a dual-role account. A
  // single-role user has `roles.length <= 1` (or no `roles` at all on an
  // older wire), so `showRoleSwitch` is false and nothing new renders —
  // zero behavioural change for them, which is the acceptance bar. The
  // active hat is `user.active_role` (falls back to `user.role`, which is
  // the same value the server resolves).
  const roles = Array.isArray(user?.roles) ? user.roles : [];
  const showRoleSwitch = roles.length > 1;
  const activeRole = user?.active_role ?? user?.role;

  // Gated on the ACTIVE hat, same variable the role switch above already
  // reads, not a separate role lookup: switching hats (`doSwitchRole`)
  // changes it on the same re-render that updates everything else this menu
  // shows for the new role.
  const isPlayer = activeRole === "player";

  const doSwitchRole = async (role) => {
    if (role === activeRole || switchingTo) return;
    setSwitchingTo(role);
    // `switchActiveRole` POSTs /auth/active-role, which reissues the session
    // cookie and returns the user wearing the new hat; AuthContext pushes it
    // into context so the nav and gates re-render. Navigate to the new
    // role's home so we don't linger on a page the new hat cannot open
    // (ProtectedRoute would otherwise bounce it) — the same reasoning
    // Layout's demo switch uses.
    const updated = await switchActiveRole(role);
    setSwitchingTo(null);
    if (updated) {
      track("menu.choose", { item: "role_switch" });
      markNav("account_menu");
      navigate(homePathForRole(role));
    }
  };

  // Issue #294's read half, moved here with the avatar it belongs to.
  // The photo arrives on the wire as `profile_photo` and is shown in the
  // empty top-bar circle too (#1507, D401); with none, the empty circle stays empty; the full avatar (old look, and the new-look Settings and Matches bars) shows the initials.
  // Never a stock photograph of a stranger — see the harness's own
  // assertion in frontend/e2e/responsive.spec.js.
  const photo = mediaUrl(user?.profile_photo);
  const initials = initialsFrom(user?.display_name) || "U";

  const doSignOut = async () => {
    track("menu.choose", { item: "sign_out" });
    setSigningOut(true);
    // `logout()` POSTs /auth/logout, which clears the httpOnly session
    // cookie server-side — so this genuinely ends the session rather than
    // forgetting it locally. `signOutAndGoHome` owns what happens next and
    // why (lib/signOut.js); all three sign-out controls call it.
    setSignOutError("");
    const result = await signOutAndGoHome(logout, { silent: true });
    // Issue #326: on success this line is unreachable in practice — the
    // navigation has already started and this component is going away with
    // the document. On FAILURE there is no navigation, so the item has to
    // become pressable again: a permanently disabled "Signing out…" on a
    // session that is still live is the same lie in a different font.
    if (result?.ok === false) {
      setSignOutError(result.error || SIGN_OUT_FAILED_MESSAGE);
      setSigningOut(false);
    }
  };

  return (
    // `modal={false}`: Radix's DropdownMenu defaults to modal, which sets
    // `pointer-events: none` on <body> and `aria-hidden` on everything
    // outside it for as long as it is open. That is right for a dialog and
    // wrong for a chrome menu — it makes the rest of the app inert behind
    // a three-item list, and any close that races a route change can leave
    // the page unclickable. The topbar's other two panels (#189's
    // notifications, AccessibilityMenu) are Radix Popovers, which are
    // non-modal by default; this makes the third one behave like its
    // neighbours. Escape, click-outside and focus return are unaffected —
    // they come from DismissableLayer either way.
    <DropdownMenu
      modal={false}
      onOpenChange={(open) => {
        // The refusal belongs to this opening of the menu; a fresh opening
        // starts clean.
        if (!open) setSignOutError("");
        else track("menu.open", { item: "account" });
      }}
    >
      <DropdownMenuTrigger asChild>
        {/* `topbar-user` stays on the control itself rather than moving to a
            wrapper: it is the id the responsive harness's overflow
            attribution (#273/#276) names when this corner of the row is the
            element hanging past the viewport, and it should keep naming the
            same box. */}
        <button
          type="button"
          data-testid="topbar-user"
          aria-label={`Account menu for ${user?.display_name || "your account"}`}
          className={
            empty
              ? // `rounded-full` REMOVED (PR #887 review, B3 follow-up) — on
                // a 44×barHeight rectangle it rounds the corners enough
                // (radius = min(width,height)/2 = 16px) that
                // `elementFromPoint` genuinely excludes them: a probe at
                // this button's own top-left corner fell through to the
                // wrapping div, confirmed with a real `page.mouse.click`
                // (the click never opened the account menu). D160 asks for
                // a plain rectangle tiling edge to edge with no overlap; a
                // rounded one breaks that exactly at the corners a probe
                // would find.
                // Issue #892 (D171 X2) — `compactFocusRingFor(surface)`
                // above replaces the shared `ring-ring`; see that helper.
                `flex shrink-0 items-center justify-center hover:bg-white/10 transition-colors ${compactFocusRingFor(surface)}`
              : "flex min-w-0 shrink items-center gap-2 sm:gap-3 rounded-full py-1 pl-1 pr-1 sm:pr-2 hover:bg-slate-100 transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          }
          // #1122 (D221 O6) — the control is the word "Account" and the 26px
          // circle, `gap: 6`, 8px of padding each side, at least a tap
          // target tall (the row stretches it to the bar's height). Before
          // this it was a 44px-wide box holding the circle alone, with the
          // circle 9px from the box's right edge and `marginRight: -6`
          // pulling that edge past the bar's padding (#1118); the circle now
          // sits 8px from the edge, so -5 keeps it exactly where it was.
          // Only here: the full avatar (non-`empty`) did not change size
          // and must not move.
          style={empty ? { minHeight: "var(--nw-size-tap)", padding: "0 8px", gap: 6, marginRight: -5 } : undefined}
        >
          {empty ? (
            /* #780 (D140) — the drawn account circle: 26px, `#E8F1FB`
               background, 1px `#CBD5E1` border, no initials, no gold ring.
               #1507 (D401): the profile photo fills it when one exists
               (reverses D140's "no photo"); with none it stays empty.
               `aria-hidden` because the button's own `aria-label` above
               already names the control; this span carries no text content
               for a screen reader to read twice. */
            <>
              {/* #1122 — the word is the control's name on screen (D221 G3:
                  a labelled button, not only an icon). 13px/600; `--nw-ink`
                  on the white bar, white on the navy one. An inline colour,
                  not a `text-*` class: `index.css` remaps `text-slate-*`
                  with `!important`. No icon beside it (the circle is the
                  only mark). */}
              <span
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  lineHeight: 1,
                  whiteSpace: "nowrap",
                  color: surface === "white" ? "var(--nw-ink)" : "#FFFFFF",
                }}
              >
                Account
              </span>
              <span
                aria-hidden="true"
                style={{
                  width: 26,
                  height: 26,
                  flexShrink: 0,
                  borderRadius: 999,
                  background: "#E8F1FB",
                  border: "1px solid #CBD5E1",
                  overflow: "hidden",
                }}
              >
                {photo && <img src={photo} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />}
              </span>
            </>
          ) : (
            <>
              {/* min-w-0 + truncate: a long display name used to set this flex
                  item's intrinsic width and push the topbar row past the
                  viewport — the un-truncated-name defect noted during #216 and
                  left open. The name is `hidden sm:block`, so the overflow it
                  caused was a ≥640px one; the cap is expressed in `ch` because
                  what must not happen is a name growing the ROW, not a name
                  exceeding some pixel count. */}
              {/* #1122 — below `sm` the name and role are hidden, so the
                  control reads "Account" there (D221 O6) and the name from
                  `sm` up. */}
              <span className="sm:hidden text-[13px] font-semibold leading-none whitespace-nowrap" style={{ color: surface === "white" ? "var(--nw-ink)" : "#FFFFFF" }}>
                Account
              </span>
              <span className="hidden sm:block min-w-0 text-right">
                <span className="block truncate max-w-[18ch] text-sm font-semibold text-slate-900">
                  {user?.display_name}
                </span>
                <span className="block text-xs text-slate-500 capitalize">{user?.role}</span>
              </span>
              {/* Issue #780 (filed off PR #777 review F3): this ring was gold
                  (`border-[#D4A017]`) — the same hex `ActionButton` uses for the
                  one primary action rule R-003 allows per screen. On EVERY
                  screen this topbar renders on, that made two gold elements at
                  once, R-003 false everywhere, not just on the redrawn set-up
                  screen. `SetUp.dc.html:60` draws this circle empty, with a
                  subtle ring, not gold — `border-slate-300` is `#CBD5E1`,
                  `--nw-line-strong`, the kit's own neutral ring colour
                  (`components/kit/TopBar.jsx`'s white-variant avatar
                  placeholder uses the same value). The lockup-truncation half
                  of #780 (`CompassLogo` at 72px) stays deferred to the desktop
                  pass. */}
              <Avatar className="h-9 w-9 shrink-0 border-2 border-slate-300">
                {photo && <AvatarImage src={photo} alt="" />}
                <AvatarFallback className="text-xs font-semibold">{initials}</AvatarFallback>
              </Avatar>
            </>
          )}
        </button>
      </DropdownMenuTrigger>

      {/* `w-[min(15rem,calc(100vw-2rem))]` + collision padding, for the same
          reason the notifications panel carries it (#189): at phone-390 a
          fixed-width panel anchored to the right edge of this row is the
          overflow class this header keeps being broken by
          (#251/#263/#273/#276). Told it may shrink, it shrinks. */}
      <DropdownMenuContent
        align="end"
        collisionPadding={8}
        className="w-[min(15rem,calc(100vw-2rem))]"
        data-testid="account-menu"
      >
        {/* The identity block is not decoration below `sm`: the trigger's
            name and role are `hidden sm:block`, so on a phone this menu is
            the only place the app tells you WHICH account you are about to
            sign out of. Shared phones are the pilot's normal case (#262). */}
        <div className="px-2 py-1.5" data-testid="account-menu-identity">
          <p className="truncate text-sm font-semibold text-slate-900">{user?.display_name}</p>
          <p className="truncate text-xs text-slate-500">{user?.email}</p>
          <p className="text-[11px] uppercase tracking-widest text-slate-400 mt-0.5">{user?.role}</p>
        </div>
        <DropdownMenuSeparator />
        {/* #1122 (D221 O6) — Settings is the first labelled item, directly
            after the identity block: Account, then Settings, two labelled
            taps (G3). */}
        <DropdownMenuItem asChild className="cursor-pointer gap-2 min-h-11">
          <Link
            to="/settings"
            onClick={() => {
              markNav("account_menu");
              track("menu.choose", { item: "settings" });
            }}
            data-testid="account-menu-settings"
          >
            <SettingsIcon size={16} />
            Settings
          </Link>
        </DropdownMenuItem>
        {/* Issue #507: the hat switch, only for a dual-role account. A
            single-role user never sees this block. Each held role is a row;
            the active one carries a check and is not actionable, the others
            switch on select (which reissues the session and re-homes the
            app). `onSelect` with `preventDefault()` so the menu does not
            close out from under the in-flight switch. */}
        {showRoleSwitch && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel
              className="text-[11px] uppercase tracking-widest text-slate-400 font-normal"
              data-testid="account-menu-role-switch"
            >
              Switch role
            </DropdownMenuLabel>
            {roles.map((role) => {
              const isActive = role === activeRole;
              return (
                <DropdownMenuItem
                  key={role}
                  disabled={isActive || switchingTo !== null}
                  onSelect={(e) => {
                    e.preventDefault();
                    doSwitchRole(role);
                  }}
                  data-testid={`account-menu-role-${role}`}
                  data-leave="true"
                  aria-current={isActive ? "true" : undefined}
                  className="cursor-pointer gap-2 min-h-11"
                >
                  <Repeat size={16} />
                  <span className="flex-1">{roleLabel(role)}</span>
                  {isActive && <Check size={16} className="text-[#0B2E59]" />}
                </DropdownMenuItem>
              );
            })}
          </>
        )}
        {/* `asChild` + a real <Link>, not an `onSelect` that calls
            `navigate()`: this item is a DESTINATION, so it should be an
            anchor. Middle-click, ⌘-click and "open in new tab" then work,
            the status bar shows where the item goes, and a screen reader
            announces a link rather than a command. Sign out below stays an
            action item, because it is one.

            Not a fix for anything: an open that lands within a few hundred
            milliseconds of the navigation an item just caused is still
            toggled straight back shut by Radix, with or without links (see
            the note in e2e/journeys/10-account-menu.journey.spec.js, where
            it was traced). That window is inside the primitive and is not
            worth reaching into it for. */}
        {/* Training & guides for a player on a screen with no ribbon (the
            old look, D217). The coach's Training item (#962, D177) is gone:
            the coach ribbon carries it (#1121, D221 O1; D214.1's "the
            account-menu item goes"). A ribbon screen (`hasRibbon`) carries
            it for the player too (#1119, D216). */}
        {isPlayer && !hasRibbon && (
          <DropdownMenuItem asChild className="cursor-pointer gap-2 min-h-11">
            <Link
              to="/learn"
              onClick={() => {
                markNav("account_menu");
                track("menu.choose", { item: "training" });
              }}
              data-testid="account-menu-training"
            >
              <BookOpen size={16} />
              Training & guides
            </Link>
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={(e) => {
            // Keep the menu open: if the sign-out is refused, the sentence
            // is drawn inside it, under this item. On success the page
            // navigates away, so staying open costs nothing.
            e.preventDefault();
            doSignOut();
          }}
          disabled={signingOut}
          data-testid="account-menu-signout"
          data-leave="true"
          className="cursor-pointer gap-2 min-h-11 text-red-600 focus:text-red-600"
        >
          <LogOut size={16} />
          {signingOut ? "Signing out…" : "Sign out"}
        </DropdownMenuItem>
        {signOutError && <AccountMenuSignOutError>{signOutError}</AccountMenuSignOutError>}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
